import { sessionSetter } from '@utils/storage';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  useLazyGetActiveContractorsListForPreferenceQuery,
  useLazyGetBusinessTypesQuery,
  useLazyGetCategoryTypesQuery,
  useLazyGetChangedByFiltrationSelectQuery,
  useLazyGetContractorsLosAllTypeQuery,
  useLazyGetContractorsQuery,
  useLazyGetHealthPlanSelectQuery,
  useLazyGetLimitsTypesQuery,
  useLazyGetModeOfTransportAllTypesQuery,
  useLazyGetNotedByFiltrationSelectQuery,
  useLazyGetPreferencesAssignmentTypeQuery,
  useLazyGetPreferenceUsersQuery,
  useLazyGetRelationshipSelectDataQuery,
  useLazyGetRideSegmentStatusSelectQuery,
  useLazyGetRideServiceCategoryTypesQuery,
  useLazyGetUserPermissionsFiltrationSelectQuery,
  useLazyGetUserTypeFiltrationSelectQuery,
  useLazyGetViewedByFiltrationSelectQuery,
} from '@store/rtk/selects';
import { selectGlobal } from '@store/slices/global/global';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SelectType } from '@uikit/Tables/Filtration/constants';
import { useLazyGetNoteCategoriesQuery } from '@store/rtk/notes';
import { X_HEADER_ID_NAME } from '@utils/constants';
import {
  selectSelects,
  setActivityStatus,
} from '@store/slices/selects/selects';

interface SelectOption {
  id: string | number | boolean;
  value: string | number | boolean;
  label: string;
}

interface InfoLabel {
  name: string;
  label: string;
}

interface FetchContext {
  currentPath: string;
  associatedId: unknown;
}

interface FetchData {
  request: (params: Record<string, any>) => Promise<{ data?: SelectOption[] }>;
  getParams?: (ctx: FetchContext) => Record<string, any>;
  info: InfoLabel;
  skipRequest?: boolean;
}

interface UseSelectOptionsReturn {
  selectOptions: SelectOption[];
  infoLabel?: InfoLabel;
}

export const useSelectOptions = (
  selectType: SelectType | undefined
): UseSelectOptionsReturn => {
  const dispatch = useAppDispatch();
  const memberHeaderIdStr: any = sessionSetter.getItem(X_HEADER_ID_NAME);

  const [getLimitsTypes] = useLazyGetLimitsTypesQuery();
  const [getNoteCategories] = useLazyGetNoteCategoriesQuery();
  const [getBusinessTypes] = useLazyGetBusinessTypesQuery();
  const [getModeOfTransportAllTypes] = useLazyGetModeOfTransportAllTypesQuery();
  const [getCategoryTypes] = useLazyGetCategoryTypesQuery();
  const [getContractorsLosAllType] = useLazyGetContractorsLosAllTypeQuery();
  const [getRideServiceCategoryTypes] =
    useLazyGetRideServiceCategoryTypesQuery();
  const [getRelationshipSelectData] = useLazyGetRelationshipSelectDataQuery();
  const [getHealthPlanSelect] = useLazyGetHealthPlanSelectQuery();
  const [getUserTypeFiltrationSelect] =
    useLazyGetUserTypeFiltrationSelectQuery();
  const [getUserPermissionsFiltrationSelect] =
    useLazyGetUserPermissionsFiltrationSelectQuery();
  const [getChangedByFiltrationSelect] =
    useLazyGetChangedByFiltrationSelectQuery();
  const [getViewedByFiltrationSelect] =
    useLazyGetViewedByFiltrationSelectQuery();
  const [getNotedByFiltrationSelect] = useLazyGetNotedByFiltrationSelectQuery();
  const [getContractors] = useLazyGetContractorsQuery();
  const [getRideSegmentStatusSelect] = useLazyGetRideSegmentStatusSelectQuery();
  const [getActiveContractorsListForPreference] =
    useLazyGetActiveContractorsListForPreferenceQuery();
  const [getPreferencesAssignmentType] =
    useLazyGetPreferencesAssignmentTypeQuery();
  const [getPreferenceUsers] = useLazyGetPreferenceUsersQuery();

  const { currentPath } = useAppSelector(selectGlobal);
  const { contractors } = useAppSelector(selectSelects);

  const [selectOptions, setSelectOptions] = useState<SelectOption[]>([]);
  const [infoLabel, setInfoLabel] = useState<InfoLabel | undefined>();

  const associatedId = memberHeaderIdStr?.[currentPath];

  const configMap = useMemo<Record<string, FetchData>>(
    () => ({
      lob: {
        request: getBusinessTypes,
        info: {
          name: 'lob',
          label: 'Line of Business',
        },
      },
      serviceCategory: {
        request: getCategoryTypes,
        info: {
          name: 'category',
          label: 'Category',
        },
      },
      rideCategoryTypes: {
        request: getRideServiceCategoryTypes,
        info: {
          name: 'ride service category',
          label: 'Ride Service Category',
        },
      },
      payerMot: {
        request: getModeOfTransportAllTypes,
        info: {
          name: 'modeOfTransport',
          label: 'Mode of Transport',
        },
      },
      memberMot: {
        request: getModeOfTransportAllTypes,
        info: {
          name: 'modeOfTransport',
          label: 'Mode of Transport',
        },
      },
      contractorMot: {
        request: getModeOfTransportAllTypes,
        info: {
          name: 'modeOfTransport',
          label: 'Mode of Transport',
        },
      },
      noteCategory: {
        request: () => getNoteCategories({ type: currentPath }),
        getParams: (ctx) => ({ type: ctx.currentPath }),
        info: {
          name: 'category',
          label: 'Note Category',
        },
      },
      createdBy: {
        request: getPreferenceUsers,
        info: {
          name: 'createdBy',
          label: 'Created By',
        },
      },
      changedBy: {
        request: getChangedByFiltrationSelect,
        getParams: (ctx) => ({
          model: ctx.currentPath,
          associatedId: ctx.associatedId,
        }),
        info: {
          name: 'changedBy',
          label: 'Changed By',
        },
      },
      viewedBy: {
        request: getViewedByFiltrationSelect,
        getParams: (ctx) => ({
          model: ctx.currentPath,
          associatedId: ctx.associatedId,
        }),
        info: {
          name: 'viewedBy',
          label: 'Viewed By',
        },
      },
      notedBy: {
        request: getNotedByFiltrationSelect,
        getParams: (ctx) => ({
          type: ctx.currentPath,
          associatedId: ctx.associatedId,
        }),
        info: {
          name: 'notedBy',
          label: 'Noted By',
        },
      },
      contactRelation: {
        request: getRelationshipSelectData,
        info: {
          name: 'type',
          label: 'Type',
        },
      },
      limitType: {
        request: getLimitsTypes,
        info: {
          name: 'limitType',
          label: 'Limit Type',
        },
      },
      userType: {
        request: getUserTypeFiltrationSelect,
        info: {
          name: 'type',
          label: 'Type',
        },
      },
      userPermissions: {
        request: getUserPermissionsFiltrationSelect,
        info: {
          name: 'permissions',
          label: 'Permissions',
        },
      },
      healthPlan: {
        request: getHealthPlanSelect,
        info: {
          name: 'healthPlan',
          label: 'Health Plan',
        },
      },
      rideContractor: {
        request: getContractors,
        skipRequest: true,
        info: {
          name: 'contractor',
          label: 'Contractor',
        },
      },
      rideStatus: {
        request: getRideSegmentStatusSelect,
        info: {
          name: 'status',
          label: 'Status',
        },
      },
      modeOfTransportType: {
        request: getContractorsLosAllType,
        info: {
          name: 'type',
          label: 'Type',
        },
      },
      preferContractor: {
        request: getActiveContractorsListForPreference,
        info: {
          name: 'contractor',
          label: 'Contractor',
        },
      },
      preferAssignmentType: {
        request: getPreferencesAssignmentType,
        info: {
          name: 'assignmentType',
          label: 'Assignment Type',
        },
      },
      activityStatus: {
        request: (): Promise<{ data?: SelectOption[] }> =>
          Promise.resolve({
            data: [
              { id: 1, value: true, label: 'Active' },
              { id: 2, value: false, label: 'Inactive' },
            ],
          }),
        info: {
          name: 'activityStatus',
          label: 'Status',
        },
      },
    }),
    [
      getBusinessTypes,
      getCategoryTypes,
      getRideServiceCategoryTypes,
      getModeOfTransportAllTypes,
      getNoteCategories,
      getChangedByFiltrationSelect,
      getViewedByFiltrationSelect,
      getNotedByFiltrationSelect,
      getRelationshipSelectData,
      getLimitsTypes,
      getUserTypeFiltrationSelect,
      getUserPermissionsFiltrationSelect,
      getHealthPlanSelect,
      getContractors,
      getRideSegmentStatusSelect,
      getContractorsLosAllType,
      getActiveContractorsListForPreference,
      getPreferencesAssignmentType,
    ]
  );

  const currentConfig = selectType ? configMap[selectType] : undefined;

  const lastFetchKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectType || !currentConfig) {
      setSelectOptions([]);
      setInfoLabel(undefined);
      lastFetchKeyRef.current = null;
      return;
    }

    if (currentConfig.skipRequest) {
      setInfoLabel(currentConfig.info);
      setSelectOptions(contractors || []);
      lastFetchKeyRef.current = null;
      return;
    }

    const fetchKey = `${selectType}:${currentPath}:${associatedId}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;

    let isMounted = true;

    const fetchData = async () => {
      try {
        setInfoLabel(currentConfig.info);
        const params = currentConfig.getParams
          ? currentConfig.getParams({ currentPath, associatedId })
          : {};
        const result = await currentConfig.request(params);

        if (isMounted) {
          const options = result.data || [];
          setSelectOptions(options);

          if (selectType === 'activityStatus') {
            dispatch(setActivityStatus(options));
          }
        }
      } catch (error) {
        if (isMounted) {
          setSelectOptions([]);
          setInfoLabel(undefined);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    currentConfig,
    selectType,
    currentPath,
    associatedId,
    contractors,
    dispatch,
  ]);

  return { selectOptions, infoLabel };
};
